import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag } from "lucide-react";
import CartItem from "./CartItem";
import EmptyState from "../common/EmptyState";
import { useStore } from "../../context/StoreProvider";
import { formatPrice } from "../../lib/utils";

const CartDrawer = () => {
  const { cart, cartCount, subtotal, cartOpen, setCartOpen } = useStore();

  useEffect(() => {
    document.body.style.overflow = cartOpen ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setCartOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [cartOpen, setCartOpen]);

  if (!cartOpen) return null;

  return (
    <div className="fixed inset-0 z-[85]" data-testid="cart-drawer">
      <div
        className="absolute inset-0 bg-ink/50 backdrop-blur-sm animate-fade-in"
        onClick={() => setCartOpen(false)}
      />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-paper animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ink px-5 py-4">
          <h2 className="font-display text-2xl uppercase tracking-tight">
            Cart ({cartCount})
          </h2>
          <button
            onClick={() => setCartOpen(false)}
            aria-label="Close cart"
            data-testid="cart-drawer-close"
            className="hover:text-brand-dark transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState
              icon={ShoppingBag}
              title="Your cart is empty"
              message="Add something you love to get started."
              ctaLabel="Shop Now"
              ctaTo="/shop"
              testId="cart-empty"
            />
          </div>
        ) : (
          <>
            <div className="flex-1 divide-y divide-ink/10 overflow-y-auto px-5">
              {cart.map((item) => (
                <CartItem key={item.lineId} item={item} compact />
              ))}
            </div>
            <div className="border-t border-ink px-5 py-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="label-mono">Subtotal</span>
                <span className="font-mono text-lg" data-testid="cart-drawer-subtotal">
                  {formatPrice(subtotal)}
                </span>
              </div>
              <p className="mb-4 text-xs text-ink/50">
                Taxes & shipping calculated at order. Order is placed via WhatsApp.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/cart"
                  onClick={() => setCartOpen(false)}
                  className="btn-outline w-full"
                  data-testid="cart-drawer-view-cart"
                >
                  View Cart
                </Link>
                <Link
                  to="/checkout"
                  onClick={() => setCartOpen(false)}
                  className="btn-solid w-full"
                  data-testid="cart-drawer-checkout"
                >
                  Proceed
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
